import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { useI18n } from '@/i18n/LanguageContext';
import Seo from '@/components/Seo';
import NotFound from '@/pages/NotFound';
import { countyBySlug, citiesInCounty, COUNTIES } from '@/data/seo/locations';
import { SEO_TRADES, MONEY_TRADES } from '@/data/seo/trades';
import { MONEY_PAGES } from '@/data/seo/moneyPages';
import { breadcrumbSchema, faqSchema, localBusinessSchema, locationFaqs, itemListSchema } from '@/lib/seoSchema';
import {
  Breadcrumbs, PageHero, AnswerBlock, Section, FaqSection, LinkGrid, CtaBand, EntityFacts, PageWrap, TrustStrip,
} from '@/components/seo/SeoBlocks';
import { LEAD_PRICE, money } from '@/data/constants';
import LiveProjects from '@/components/seo/LiveProjects';

const CountyPage: React.FC = () => {
  const { slug = '' } = useParams();
  const { lang, path, t } = useI18n();
  const county = countyBySlug(slug);
  if (!county) return <NotFound />;

  const label = `${county.name} County`;
  const faqs = locationFaqs(county.name, 'county', lang);
  const url = `/${lang}/california/counties/${county.slug}`;
  const cities = citiesInCounty(county.slug);

  const cityLinks = cities.map((c) => ({ label: `${c.name}, CA`, to: path(`/california/${c.slug}`) }));
  const tradeLinks = SEO_TRADES.slice(0, 24).map((tr) => ({
    label: lang === 'es' ? tr.es : tr.en,
    to: path(`/trades/${tr.slug}`),
  }));
  const leadLinks = MONEY_PAGES.filter((m) => m.countySlug === county.slug && m.scope === 'county')
    .map((m) => ({ label: `${m.trade.en} Leads ${m.place}`, to: path(`/leads/${m.slug}`) }));
  const nearbyCounties = COUNTIES.filter((x) => x.region === county.region && x.slug !== county.slug)
    .slice(0, 10)
    .map((x) => ({ label: `${x.name} County`, to: path(`/california/counties/${x.slug}`) }));

  const intro =
    lang === 'es'
      ? `Proyectos de construcción activos en el Condado de ${county.name}, California. Los propietarios y contratistas generales publican el trabajo; los subcontratistas verificados desbloquean la ubicación exacta y los contactos por ${money(LEAD_PRICE)}.`
      : `Active construction projects across ${label}, California. Homeowners and general contractors post the work; verified subcontractors unlock the exact location and contact details for ${money(LEAD_PRICE)}.`;

  return (
    <>
      <Seo
        title={lang === 'es' ? `Proyectos de Construcción en el Condado de ${county.name}` : `${label} Construction Projects & Leads`}
        description={
          lang === 'es'
            ? `Vea proyectos de construcción en el Condado de ${county.name} (${county.region}). ${cities.length} ciudades, todos los oficios, leads a ${money(LEAD_PRICE)}.`
            : `Browse construction projects in ${label} (${county.region}) — ${cities.length} cities, every trade, flat ${money(LEAD_PRICE)} leads for subcontractors.`
        }
        schema={[
          breadcrumbSchema([
            { name: 'Home', url: `/${lang}` },
            { name: 'California', url: `/${lang}/california` },
            { name: label, url },
          ]),
          localBusinessSchema({ name: label, area: `${label}, California`, url }),
          faqSchema(faqs),
          itemListSchema(`Cities in ${label}`, cityLinks.map((c) => ({ name: c.label, url: c.to }))),
        ]}
      />

      <PageHero eyebrow={t('seo.countyEyebrow')} title={lang === 'es' ? `Condado de ${county.name}, California` : `${label}, California`} intro={intro}>
        <Link to={path('/jobs')} className="rounded-xl bg-orange-500 px-5 py-3 text-sm font-bold hover:bg-orange-600">
          {t('seo.ctaBrowse')}
        </Link>
        <Link to={path('/register')} className="rounded-xl border border-white/20 px-5 py-3 text-sm font-bold hover:bg-white/10">
          {t('seo.ctaSubBtn')}
        </Link>
      </PageHero>

      <PageWrap>
        <Breadcrumbs
          items={[
            { name: t('nav.home'), url: path('/') },
            { name: 'California', url: path('/california') },
            { name: label },
          ]}
        />

        <AnswerBlock question={faqs[0].q} answer={faqs[0].a} />

        <EntityFacts
          rows={[
            { k: t('seo.factCounty'), v: label },
            { k: t('seo.factRegion'), v: county.region },
            { k: t('seo.factCities'), v: String(cities.length) },
            { k: t('seo.factPrice'), v: money(LEAD_PRICE) },
          ]}
        />

        <Section title={t('seo.opportunityOverview')}>
          <p>{intro}</p>
          <p>{t('seo.countyBody', { county: label, region: county.region, pop: county.pop })}</p>
        </Section>

        <Section title={t('seo.activeTrades')}>
          <ul className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
            {MONEY_TRADES.slice(0, 12).map((tr) => (
              <li key={tr.slug}>
                <Link
                  to={path(`/trades/${tr.slug}`)}
                  className="block rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:border-orange-300 hover:text-orange-600"
                >
                  {lang === 'es' ? tr.es : tr.en}
                </Link>
              </li>
            ))}
          </ul>
          <TrustStrip />
        </Section>

        <LiveProjects state="CA" heading={t('seo.recentProjects', { place: label })} />

        <FaqSection faqs={faqs} />

        <LinkGrid title={t('seo.citiesIn', { place: label })} links={cityLinks} icon="map" />
        <LinkGrid title={t('seo.leadPagesPlace', { place: label })} links={leadLinks} icon="map" />
        <LinkGrid title={t('seo.nearbyCounties')} links={nearbyCounties} icon="map" />
        <LinkGrid title={t('seo.allTrades')} links={tradeLinks} icon="trade" />

        <CtaBand />
      </PageWrap>
    </>
  );
};

export default CountyPage;
